import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { clearCustomTableFilters } from "@src/features/table_filtering/utils/base_state_handler_utils";
import {
  ColumnConfig,
  RequestOverviewDateFilters,
  RequestOverviewState,
  RequestOverviewTableFilters,
} from "@src/types";
import { TabItem } from "@src/types/antd_types";

const initialState: RequestOverviewState = {
  selectedRequestId: undefined,
  modalSettings: {
    detailsModalVisible: false,
    activeTabKey: "0",
  },
  requestOverviewTableState: {
    quickFilter: "",
    filterResult: [],
    customTableFilters: {
      region: [],
      country: [],
      technologies: [],
      status: [],
      projectPhase: [],
      projectName: [],
      requestedRoleName: [],
    },
    customDateFilters: {
      roleRequestStart: null,
      requestSentDate: null,
    },
    columns: [],
    currentTabConfig: null,
    isFilterApplied: false,
    columnConfigs: [],
  },
};

const checkIfFilterApplied = (state: RequestOverviewState) => {
  const { quickFilter, customTableFilters, customDateFilters } =
    state.requestOverviewTableState;
  const customFilters = Object.values(customTableFilters).flat();
  const dateFilters = Object.values(customDateFilters).flat();

  return (
    quickFilter !== "" || dateFilters?.length > 0 || customFilters.length > 0
  );
};

const requestOverviewSlice = createSlice({
  name: "requestOverview",
  initialState: initialState,
  reducers: {
    /**
     * Sets the quick filter state to the new state.
     *
     * @param state       the request overview filter state with all values.
     * @param quickSearch the quick search string.
     */
    setQuickFilter: (
      state: RequestOverviewState,
      quickSearch: PayloadAction<string>
    ) => {
      state.requestOverviewTableState.quickFilter = quickSearch.payload;
      state.requestOverviewTableState.isFilterApplied =
        checkIfFilterApplied(state);
    },
    /**
     * Sets the custom filter state to the new state.
     *
     **
     * @param state                   the request overview filter state with all values.
     * @param action
     */
    setCustomTableFilter: (
      state: RequestOverviewState,
      action: PayloadAction<RequestOverviewTableFilters>
    ) => {
      state.requestOverviewTableState.customTableFilters = action.payload;
      state.requestOverviewTableState.isFilterApplied =
        checkIfFilterApplied(state);
    },

    /**
     * Sets the custom filter state to the new state.
     *
     **
     * @param state                   the request overview filter state with all values.
     * @param action
     */
    setCustomDateFilter: (
      state: RequestOverviewState,
      action: PayloadAction<RequestOverviewDateFilters>
    ) => {
      state.requestOverviewTableState.customDateFilters = action.payload;
      state.requestOverviewTableState.isFilterApplied =
        checkIfFilterApplied(state);
    },

    /**
     *  Sets the request overview filter result to the new state.
     *
     * @param state
     * @param action
     */
    setFilterResult: (state: RequestOverviewState, action) => {
      state.requestOverviewTableState.filterResult = action.payload;
      state.requestOverviewTableState.isFilterApplied =
        checkIfFilterApplied(state);
    },

    /**
     * Sets the current tab config to the new state.
     *
     * @param state
     * @param tabConfig
     */
    setCurrentTabConfig: (
      state: RequestOverviewState,
      tabConfig: PayloadAction<TabItem>
    ) => {
      state.requestOverviewTableState.currentTabConfig = tabConfig.payload;
    },

    /**
     * Sets the columns to the new state.
     *
     * @param state
     * @param columns
     */
    setColumns: (
      state: RequestOverviewState,
      columns: PayloadAction<string[]>
    ) => {
      state.requestOverviewTableState.columns = columns.payload;
    },

    /**
     * Sets the column configs to the new state.
     *
     * @param state
     * @param columnConfigs
     */
    setColumnConfigs: (
      state: RequestOverviewState,
      columnConfigs: PayloadAction<ColumnConfig[]>
    ) => {
      state.requestOverviewTableState.columnConfigs = columnConfigs.payload;
    },

    setDetailsModalVisible: (
      state: RequestOverviewState,
      detailsModalVisible: PayloadAction<boolean>
    ) => {
      state.modalSettings.detailsModalVisible = detailsModalVisible.payload;
      state.modalSettings.activeTabKey = "0";
    },

    setActiveTabKey: (
      state: RequestOverviewState,
      activeTabKey: PayloadAction<string>
    ) => {
      state.modalSettings.activeTabKey = activeTabKey.payload;
    },

    setSelectedRequestId: (
      state: RequestOverviewState,
      selectedRequestId: PayloadAction<number>
    ) => {
      state.selectedRequestId = selectedRequestId.payload;
    },

    /**
     * Clears a specific property from the custom table filter.
     *
     * @param state The current filter state.
     * @param action Contains the key and value to be cleared.
     * @returns Updated state with the specified filter value removed.
     */
    clearCustomTableFilterProperty: (
      state: RequestOverviewState,
      action: { payload: { key: string; value: string } }
    ): RequestOverviewState => {
      const { key, value } = action.payload;
      return {
        ...state,
        requestOverviewTableState: {
          ...state.requestOverviewTableState,
          customTableFilters: clearCustomTableFilters(
            state.requestOverviewTableState.customTableFilters,
            key,
            value
          ),
        },
      };
    },

    /**
     * Clears the filter state to the initial state.
     *
     */
    clearFilter: (state: RequestOverviewState): RequestOverviewState => {
      return {
        ...initialState,
        requestOverviewTableState: {
          ...initialState.requestOverviewTableState,
          columns: state.requestOverviewTableState.columns, // preserve the columns
          quickFilter: state.requestOverviewTableState.quickFilter, // preserve the quick filter
          currentTabConfig: state.requestOverviewTableState.currentTabConfig, // preserve the current tab config
        },
      };
    },
  },
});

export const {
  clearFilter,
  setColumnConfigs,
  setQuickFilter,
  setCurrentTabConfig,
  setCustomTableFilter,
  setCustomDateFilter,
  setFilterResult,
  setColumns,
  setSelectedRequestId,
  setDetailsModalVisible,
  setActiveTabKey,
  clearCustomTableFilterProperty,
} = requestOverviewSlice.actions;

export default requestOverviewSlice;
